import { useMemo, useCallback, useRef } from 'react';
import moment from 'moment-timezone';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';

export const useTimezone = () => {
  const { currentUserProfile } = useCurrentUserProfile();

  const primaryTz = currentUserProfile?.primaryTimezone;
  const primaryTzName = currentUserProfile?.primaryTimezone?.name;
  const secondaryTz = currentUserProfile?.secondaryTimezone;
  const secondaryTzName = currentUserProfile?.secondaryTimezone?.name;
  const isSecondaryTzEnabled = currentUserProfile?.isSecondaryTimezoneEnabled ?? false;

  const isCalendarTzEnabled = useMemo(() => isSecondaryTzEnabled && !!primaryTzName && !!secondaryTzName, [
    isSecondaryTzEnabled,
    primaryTzName,
    secondaryTzName,
  ]);

  const selectedTz = useMemo(() => {
    const candidate = isCalendarTzEnabled ? primaryTzName : null;
    if (candidate && moment.tz.zone(candidate)) return candidate;
    return moment.tz.guess();
  }, [isCalendarTzEnabled, primaryTzName]);

  const timezoneRef = useRef(selectedTz);
  timezoneRef.current = selectedTz;

  // date=undefined must call moment.tz(tz) not moment.tz(undefined, tz)
  // the two-arg form with undefined input produces an invalid moment in some versions
  const momentTz = useCallback(
    (date?: moment.MomentInput) => (date !== undefined ? moment.tz(date, timezoneRef.current) : moment.tz(timezoneRef.current)),
    [],
  );

  const now = useCallback(() => moment.tz(timezoneRef.current), []);

  const isPast = useCallback(
    (date: moment.MomentInput) => moment.tz(date, timezoneRef.current).isBefore(moment.tz(timezoneRef.current)),
    [],
  );

  const isFuture = useCallback(
    (date: moment.MomentInput) => moment.tz(date, timezoneRef.current).isAfter(moment.tz(timezoneRef.current)),
    [],
  );

  const isSameDay = useCallback(
    (a: moment.MomentInput, b: moment.MomentInput) =>
      moment.tz(a, timezoneRef.current).isSame(moment.tz(b, timezoneRef.current), 'day'),
    [],
  );

  const startOf = useCallback(
    (date: moment.MomentInput, unit: moment.unitOfTime.StartOf) => moment.tz(date, timezoneRef.current).startOf(unit),
    [],
  );

  const endOf = useCallback(
    (date: moment.MomentInput, unit: moment.unitOfTime.StartOf) => moment.tz(date, timezoneRef.current).endOf(unit),
    [],
  );

  const formatTz = useCallback((date: moment.MomentInput, fmt: string) => moment.tz(date, timezoneRef.current).format(fmt), []);

  return {
    selectedTz,
    primaryTz,
    secondaryTz,
    isCalendarTzEnabled,
    momentTz,
    now,
    isPast,
    isFuture,
    isSameDay,
    startOf,
    endOf,
    formatTz,
  };
};
